/**
 * Ted #30 (#1109, lane LR) — "Make it recurring": Gingr's "Add Recurring
 * Dates" controls inside Quick Book, the vaccine preview per date, and the
 * per-date result.
 *
 * Ted (2026-09-30): "Creating recurring reservations for selected weekdays
 * or every day through a chosen end date · Clearly identifying any
 * vaccination issue affecting future reservations."
 *
 * Rendered INSIDE the Quick Book panel (the modal swaps its content — no
 * modal-on-modal, DESIGN-PHILOSOPHY anti-patterns). The draft — customer,
 * service, dogs, time, notes, transport, the heat answers the desk already
 * took, the notify choice — comes from Quick Book; this dialog adds the
 * rule and shows the server's plan. Three steps, one primary each:
 *   1. the rule → Preview (writes nothing; the server plans the dates and
 *      names, per date, the vaccine that will be expired or missing);
 *   2. the plan → Create (every occurrence through the gated create);
 *   3. the result → Done (created / needs staff review / not booked, each
 *      date with its reason — nothing collapses to a count).
 *
 * The cap (60 dates / 366 days) is the server's and is stated on the form.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  seriesApi,
  type AdminCustomerDog,
  type HeatDeclarations,
  type SeriesCreateResponse,
  type SeriesPreviewResponse,
  type SeriesRuleInput,
  type ServiceTypeSummary,
  type TransportMode,
} from '../../lib/api.js';
import { formatReservationDate } from './confirm-pending.js';
import './Recurring.css';

/** Mo–Su in display order with the JS weekday number each maps to (0 = Sunday). */
export const WEEKDAY_OPTIONS: ReadonlyArray<{ weekday: number; short: string; long: string }> = [
  { weekday: 1, short: 'Mo', long: 'Monday' },
  { weekday: 2, short: 'Tu', long: 'Tuesday' },
  { weekday: 3, short: 'We', long: 'Wednesday' },
  { weekday: 4, short: 'Th', long: 'Thursday' },
  { weekday: 5, short: 'Fr', long: 'Friday' },
  { weekday: 6, short: 'Sa', long: 'Saturday' },
  { weekday: 0, short: 'Su', long: 'Sunday' },
];

export interface RecurringDraft {
  customer: { id: string; name: string };
  serviceType: ServiceTypeSummary;
  dogs: AdminCustomerDog[];
  /** YYYY-MM-DD — Quick Book's date becomes the first date. */
  startDate: string;
  startTime?: string;
  notes?: string;
  transport: TransportMode;
  heatDeclarations: HeatDeclarations;
  notifyCustomer: boolean;
  /** Sawyer 09 pet-inactivity (D1, R8) — Quick Book's latched #718 override, if the desk typed one. */
  assessmentOverrideReason?: string;
}

export interface RecurringBookingDialogProps {
  apiUrl: string;
  draft: RecurringDraft;
  /** Back to the single-booking form. */
  onBack: () => void;
  /** The series is created (at least one date landed, or none — the desk read the result). */
  onDone: (seriesId: string | null, firstBookingId: string | null) => void;
}

type EndMode = 'until' | 'count';

/** The rule as the server takes it. Exported for the tests. */
export function buildRule(args: {
  startDate: string;
  everyDay: boolean;
  weekdays: number[];
  intervalWeeks: 1 | 2 | 4;
  endMode: EndMode;
  untilDate: string;
  occurrenceCount: number;
}): SeriesRuleInput {
  return {
    startDate: args.startDate,
    everyDay: args.everyDay,
    weekdays: args.everyDay ? [] : args.weekdays,
    intervalWeeks: args.intervalWeeks,
    ...(args.endMode === 'until' ? { untilDate: args.untilDate } : { occurrenceCount: args.occurrenceCount }),
  };
}

/** What the plan says about one date, in the desk's words. */
export function issueLine(issues: SeriesPreviewResponse['occurrences'][number]['vaccine_issues']): string | null {
  if (issues.length === 0) return null;
  return issues
    .map((i) => `${i.dog_name}: ${i.vaccine} ${i.status === 'missing' ? 'not on file' : `expired${i.expires_at ? ` ${formatReservationDate(i.expires_at)}` : ''}`}`)
    .join(' · ');
}

/** The one-sentence outcome banner after Create. */
export function summarizeSeriesOutcome(counts: SeriesCreateResponse['counts']): { text: string; tone: 'success' | 'warning' } {
  const parts: string[] = [];
  if (counts.created > 0) parts.push(`${counts.created} booked${counts.confirmed > 0 ? ' and confirmed' : ''}`);
  if (counts.held > 0) parts.push(`${counts.held} need${counts.held === 1 ? 's' : ''} staff review`);
  if (counts.refused > 0) parts.push(`${counts.refused} not booked`);
  const text = parts.length === 0 ? 'Nothing was booked.' : `${parts.join(', ')}.`;
  return { text, tone: counts.refused > 0 || counts.held > 0 || counts.created === 0 ? 'warning' : 'success' };
}

export function RecurringBookingDialog({ apiUrl, draft, onBack, onDone }: RecurringBookingDialogProps): JSX.Element {
  const startWeekday = useMemo(() => new Date(`${draft.startDate}T00:00:00.000Z`).getUTCDay(), [draft.startDate]);
  const [everyDay, setEveryDay] = useState(false);
  const [weekdays, setWeekdays] = useState<number[]>([startWeekday]);
  const [intervalWeeks, setIntervalWeeks] = useState<1 | 2 | 4>(1);
  const [endMode, setEndMode] = useState<EndMode>('count');
  const [untilDate, setUntilDate] = useState('');
  const [occurrenceCount, setOccurrenceCount] = useState(8);
  const [dropOffTime, setDropOffTime] = useState(draft.startTime ?? '');
  const [pickUpTime, setPickUpTime] = useState('');

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<SeriesPreviewResponse | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<SeriesCreateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rule = buildRule({ startDate: draft.startDate, everyDay, weekdays, intervalWeeks, endMode, untilDate, occurrenceCount });
  const ruleKey = JSON.stringify({ rule, dropOffTime, pickUpTime });
  const previewStale = preview !== null && previewKey !== ruleKey;

  const blockedReason: string | null =
    !everyDay && weekdays.length === 0
      ? 'Pick at least one weekday, or choose every day.'
      : endMode === 'until' && !untilDate
        ? 'Pick the Until date.'
        : endMode === 'count' && (!Number.isInteger(occurrenceCount) || occurrenceCount < 1)
          ? 'Say how many dates.'
          : null;

  const toggleDay = useCallback((weekday: number) => {
    setWeekdays((prev) => (prev.includes(weekday) ? prev.filter((d) => d !== weekday) : [...prev, weekday].sort()));
  }, []);

  const body = () => ({
    customerId: draft.customer.id,
    serviceTypeId: draft.serviceType.id,
    dogIds: draft.dogs.map((d) => d.id),
    rule,
    ...(draft.startTime ? { startTime: draft.startTime } : {}),
    ...(dropOffTime ? { dropOffTime } : {}),
    ...(pickUpTime ? { pickUpTime } : {}),
    ...(draft.notes ? { notes: draft.notes } : {}),
    ...(draft.transport !== 'none' ? { transport: draft.transport } : {}),
    ...(Object.keys(draft.heatDeclarations).length > 0 ? { heatDeclarations: draft.heatDeclarations } : {}),
  });

  async function handlePreview(): Promise<void> {
    if (blockedReason) return;
    setPreviewing(true);
    setError(null);
    const res = await seriesApi.preview(apiUrl, body());
    setPreviewing(false);
    if (res.data) {
      setPreview(res.data);
      setPreviewKey(ruleKey);
      return;
    }
    setPreview(null);
    setError(res.error ?? 'We could not plan those dates.');
  }

  async function handleCreate(): Promise<void> {
    if (!preview || previewStale) return;
    setCreating(true);
    setError(null);
    const res = await seriesApi.create(apiUrl, {
      ...body(),
      notifyCustomer: draft.notifyCustomer,
      ...(draft.assessmentOverrideReason ? { assessmentOverrideReason: draft.assessmentOverrideReason } : {}), // Sawyer 09 (D1, R8)
    });
    setCreating(false);
    if (res.data) {
      setResult(res.data);
      return;
    }
    setError(res.error ?? 'We could not create the series.');
  }

  const dogNames = draft.dogs.map((d) => d.name).join(', ');
  const issueCount = preview ? preview.occurrences.filter((o) => o.vaccine_issues.length > 0).length : 0;

  // ── step 3: the result ──────────────────────────────────────────────
  if (result) {
    const banner = summarizeSeriesOutcome(result.counts);
    const first = result.occurrences.find((o) => o.booking_id)?.booking_id ?? null;
    return (
      <section className="ferro-quickbook__section" aria-label="Recurring booking result" data-testid="recurring-result">
        <span className="ferro-quickbook__section-label">Recurring booking — {result.rule_text}</span>
        <p
          className={`ferro-recurring__summary ferro-recurring__summary--${banner.tone}`}
          role={banner.tone === 'warning' ? 'alert' : 'status'}
          data-testid="recurring-result-summary"
        >
          {dogNames} · {draft.serviceType.display_name}: {banner.text}
          {draft.notifyCustomer && result.counts.confirmed > 0
            ? ' One confirmation covers the whole series.'
            : ''}
        </p>
        <ul className="ferro-recurring__plan" aria-label="Dates" data-testid="recurring-result-list">
          {result.occurrences.map((o) => {
            const issue = issueLine(o.vaccine_issues);
            return (
              <li
                key={o.date}
                className={`ferro-recurring__plan-row ferro-recurring__plan-row--${o.outcome}`}
                data-testid={`recurring-result-${o.date}`}
                data-outcome={o.outcome}
              >
                <span className="ferro-recurring__plan-date">{formatReservationDate(o.date)}</span>
                {o.outcome === 'created' ? <span className="ferro-recurring__plan-note">{o.status === 'confirmed' ? 'Confirmed' : 'Booked — pending'}</span> : null}
                {o.outcome === 'held' ? <span className="ferro-recurring__plan-issue">Needs staff review: {o.hold_reason}</span> : null}
                {o.outcome === 'refused' ? <span className="ferro-recurring__plan-refusal">Not booked — {o.refusal?.message ?? 'this date could not be booked.'}</span> : null}
                {issue ? <span className="ferro-recurring__plan-issue">Vaccination: {issue}</span> : null}
              </li>
            );
          })}
        </ul>
        <footer className="ferro-recurring__actions">
          <button type="button" className="ferro-btn ferro-btn--primary" onClick={() => onDone(result.series.id, first)} data-testid="recurring-done">
            Done
          </button>
        </footer>
      </section>
    );
  }

  // ── steps 1 + 2: the rule and the plan ──────────────────────────────
  return (
    <>
      <section className="ferro-quickbook__section" data-testid="recurring-rule">
        <span className="ferro-quickbook__section-label">Repeat for {dogNames} · {draft.serviceType.display_name}</span>
        <p className="ferro-recurring__lede">
          First date {formatReservationDate(draft.startDate)}. Pick the weekdays, how often, and when to stop — the server checks every date before anything is booked.
        </p>

        <fieldset className="ferro-field" data-testid="recurring-days">
          <legend className="ferro-field__label">Begin on which days</legend>
          <div className="ferro-recurring__days" role="group" aria-label="Weekdays">
            {WEEKDAY_OPTIONS.map((o) => {
              const on = !everyDay && weekdays.includes(o.weekday);
              return (
                <button
                  key={o.weekday}
                  type="button"
                  className={`ferro-recurring__day${on ? ' ferro-recurring__day--on' : ''}`}
                  aria-pressed={on}
                  aria-label={o.long}
                  disabled={everyDay}
                  onClick={() => toggleDay(o.weekday)}
                  data-testid={`recurring-day-${o.weekday}`}
                >
                  {o.short}
                </button>
              );
            })}
          </div>
          <label className="ferro-field ferro-field--inline">
            <input type="checkbox" checked={everyDay} onChange={(e) => setEveryDay(e.target.checked)} data-testid="recurring-every-day" />
            <span>Every day</span>
          </label>
        </fieldset>

        <div className="ferro-recurring__row">
          <label className="ferro-field">
            <span className="ferro-field__label">How often</span>
            <select
              className="ferro-field__input"
              value={intervalWeeks}
              onChange={(e) => setIntervalWeeks(Number(e.target.value) as 1 | 2 | 4)}
              data-testid="recurring-interval"
            >
              <option value={1}>Every week</option>
              <option value={2}>Every 2 weeks</option>
              <option value={4}>Every 4 weeks</option>
            </select>
          </label>
          <fieldset className="ferro-field" data-testid="recurring-end">
            <legend className="ferro-field__label">Stop</legend>
            <div className="ferro-recurring__end">
              <label className="ferro-recurring__end-option">
                <input type="radio" name="recurring-end" value="count" checked={endMode === 'count'} onChange={() => setEndMode('count')} data-testid="recurring-end-count" />
                <span>Number of dates</span>
                <input
                  type="number"
                  className="ferro-field__input"
                  min={1}
                  max={60}
                  value={occurrenceCount}
                  disabled={endMode !== 'count'}
                  onChange={(e) => setOccurrenceCount(Number(e.target.value))}
                  aria-label="Number of dates"
                  data-testid="recurring-count"
                  style={{ width: '5rem' }}
                />
              </label>
              <label className="ferro-recurring__end-option">
                <input type="radio" name="recurring-end" value="until" checked={endMode === 'until'} onChange={() => setEndMode('until')} data-testid="recurring-end-until" />
                <span>Until</span>
                <input
                  type="date"
                  className="ferro-field__input"
                  value={untilDate}
                  min={draft.startDate}
                  disabled={endMode !== 'until'}
                  onChange={(e) => setUntilDate(e.target.value)}
                  aria-label="Until date"
                  data-testid="recurring-until"
                />
              </label>
            </div>
            <span className="ferro-quickbook__hint">At most 60 dates and 366 days per series — split a longer run in two.</span>
          </fieldset>
        </div>

        <div className="ferro-recurring__row">
          <label className="ferro-field">
            <span className="ferro-field__label">Drop-off time</span>
            <input type="time" className="ferro-field__input" value={dropOffTime} onChange={(e) => setDropOffTime(e.target.value)} data-testid="recurring-drop-off" />
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">Pick-up time</span>
            <input type="time" className="ferro-field__input" value={pickUpTime} onChange={(e) => setPickUpTime(e.target.value)} data-testid="recurring-pick-up" />
          </label>
        </div>
      </section>

      {/* ── the plan ── */}
      <section className="ferro-quickbook__section" aria-live="polite" data-testid="recurring-plan">
        <span className="ferro-quickbook__section-label">
          {preview ? `${preview.dates.length} date${preview.dates.length === 1 ? '' : 's'} — ${preview.rule_text}` : 'Dates'}
        </span>
        {previewing ? (
          <ul className="ferro-recurring__plan" aria-busy="true" data-testid="recurring-plan-skeleton">
            <li className="ferro-recurring__skeleton" />
            <li className="ferro-recurring__skeleton" />
            <li className="ferro-recurring__skeleton" />
          </ul>
        ) : preview ? (
          <>
            {issueCount > 0 ? (
              <p className="ferro-recurring__summary ferro-recurring__summary--warning" role="status" data-testid="recurring-vaccine-summary">
                A vaccination record will be out of date on {issueCount} of these dates. The dates still book; the client is told once for the whole series.
              </p>
            ) : (
              <p className="ferro-recurring__summary ferro-recurring__summary--success" role="status" data-testid="recurring-vaccine-ok">
                Vaccination records cover every date.
              </p>
            )}
            {previewStale ? (
              <p className="ferro-quickbook__hint" role="status" data-testid="recurring-plan-stale">
                The rule changed — preview again before creating.
              </p>
            ) : null}
            <ul className="ferro-recurring__plan" aria-label="Planned dates" data-testid="recurring-plan-list">
              {preview.occurrences.map((o) => {
                const issue = issueLine(o.vaccine_issues);
                return (
                  <li
                    key={o.date}
                    className={`ferro-recurring__plan-row${issue ? ' ferro-recurring__plan-row--issue' : ''}`}
                    data-testid={`recurring-plan-${o.date}`}
                  >
                    <span className="ferro-recurring__plan-date">{formatReservationDate(o.date)}</span>
                    {issue ? <span className="ferro-recurring__plan-issue">Vaccination: {issue}</span> : <span className="ferro-recurring__plan-note">Ready to book</span>}
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <p className="ferro-quickbook__hint" data-testid="recurring-plan-empty">
            Preview to see every date and any vaccination problem before booking.
          </p>
        )}
        {error ? (
          <p className="ferro-recurring__error" role="alert" data-testid="recurring-error">
            {error}
          </p>
        ) : null}
      </section>

      <footer className="ferro-recurring__actions">
        {blockedReason && !previewing ? (
          <span className="ferro-recurring__blocked" role="status">
            {blockedReason}
          </span>
        ) : null}
        <button type="button" className="ferro-btn ferro-btn--ghost" onClick={onBack} data-testid="recurring-back">
          Back
        </button>
        {!preview || previewStale ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            disabled={blockedReason !== null || previewing}
            aria-busy={previewing}
            onClick={() => void handlePreview()}
            data-testid="recurring-preview"
          >
            {previewing ? 'Planning…' : 'Preview dates'}
          </button>
        ) : (
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            disabled={creating}
            aria-busy={creating}
            onClick={() => void handleCreate()}
            data-testid="recurring-create"
          >
            {creating ? 'Booking…' : `Book ${preview.dates.length} date${preview.dates.length === 1 ? '' : 's'}`}
          </button>
        )}
      </footer>
    </>
  );
}
